import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  Modal,
  Button,
  message,
  Slider,
  Spin,
  ColorPicker,
  Checkbox,
  Space,
  Tooltip,
} from 'antd';
import {
  BgColorsOutlined,
  AimOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import Cropper from 'react-easy-crop';
import IconLibraryPicker from './IconLibraryPicker';

const ImageUploader = ({
  token,
  width = 1920,
  height = 1080,
  objectFit = 'cover',
  s3BaseUrl,
  prefix,
  thumbPrefix,
  backgroundColor: defaultBackgroundColor,
  buttonText,
  onChange,
}) => {
  const [sourceModal, setSourceModal] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  const [cropModalVisible, setCropModalVisible] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState(
    defaultBackgroundColor || '#919191ff'
  );
  const [snapTheFrame, setSnapTheFrame] = useState(true);

  const fileInputRef = useRef(null);
  const cropperRef = useRef(null);

  const libraryType = libraryOpen; // 'user' | 'icon' | 'common'

  const ratioText = useMemo(() => {
    const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
    const g = gcd(width, height);
    return `${width / g}:${height / g}`;
  }, [width, height]);

  const ratioKey = useMemo(() => {
    return ['16:9', '1:1', '3:1'].includes(ratioText) ? ratioText : null;
  }, [ratioText]);

  const openSourceSelector = () => setSourceModal(true);

  const openCropModal = (src, file = null) => {
    setCurrentFile(file);
    setImageSrc(src);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCropModalVisible(true);
  };

  const urlToDataUrl = async (url) => {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/private/image/prepare`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ imageUrl: url }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || `Failed to prepare image: ${response.status}`);
    }

    const data = await response.json();
    if (!data?.dataUrl) {
      throw new Error('Prepared image data is missing');
    }

    return data.dataUrl;
  };

  const handleLibrarySelect = async (url) => {
    setLibraryOpen(false);
    setSourceModal(false);

    if (libraryType === 'icon') {
      onChange?.(url);
      message.success('Selected icon');
      return;
    }

    let finalUrl = url;
    if (url.includes('/thumb/')) {
      finalUrl = url.replace('/thumb/', '/full/');
    }

    try {
      const base64 = await urlToDataUrl(finalUrl);
      openCropModal(base64);
    } catch (error) {
      console.error('Failed to prepare library image for cropping:', error);
      message.error(error.message || 'Failed to prepare image for cropping.');
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      openCropModal(reader.result, file);
    };

    e.target.value = '';
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = reject;
    });

  const getCroppedBlob = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return null;

    const img = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const { x, y, width: cropWidth, height: cropHeight } = croppedAreaPixels;

    // Never draw the crop selection into a canvas larger than the source
    // pixels actually available - doing so (the old behavior: always
    // width x height, e.g. 1080x340) silently upscales whatever the admin
    // selected and bakes that blur permanently into the saved PNG. The
    // backend's own resize step already refuses to upscale (sharp with
    // withoutEnlargement: true), but it never gets the chance to help once
    // the client has already stretched a low-res crop up to full size.
    const outputWidth = Math.max(1, Math.min(width, Math.round(cropWidth)));
    const outputHeight = Math.max(1, Math.min(height, Math.round(cropHeight)));

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, outputWidth, outputHeight);

    ctx.drawImage(img, x, y, cropWidth, cropHeight, 0, 0, outputWidth, outputHeight);

    return new Promise((resolve) =>
      canvas.toBlob((blob) => resolve(blob), 'image/png')
    );
  }, [backgroundColor, croppedAreaPixels, height, imageSrc, width]);

  const isLowResolutionCrop =
    !!croppedAreaPixels &&
    (croppedAreaPixels.width < width || croppedAreaPixels.height < height);

  const handleUpload = async () => {
    setUploading(true);

    try {
      const blob = await getCroppedBlob();
      if (!blob) {
        message.error('Crop failed');
        setUploading(false);
        return;
      }

      const safeName = currentFile?.name
        ? currentFile.name.replace(/\s+/g, '_')
        : `cropped_${Date.now()}.png`;

      const file = new File([blob], safeName, { type: 'image/png' });

      const formData = new FormData();
      formData.append('image', file);
      formData.append('width', String(width));
      formData.append('height', String(height));
      formData.append('objectFit', objectFit);

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/private/image/upload`,
        {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result?.error || `Upload failed (${res.status})`);
      }

      const url = result?.Location || result?.fileUrl;

      if (url) {
        onChange?.(url);
        message.success('Upload success');
        setCropModalVisible(false);
      } else {
        message.error('Upload failed: server did not return an image URL');
      }
    } catch (error) {
      console.error(error);
      message.error(error.message || 'Upload error');
    } finally {
      setUploading(false);
    }
  };

  const handleScreenPick = async () => {
    if (!('EyeDropper' in window)) {
      message.warning('Screen colour picker is not supported');
      return;
    }

    try {
      const eye = new window.EyeDropper();
      const res = await eye.open();
      if (res?.sRGBHex) setBackgroundColor(res.sRGBHex);
    } catch {
      // ignored
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      <Button icon={<PictureOutlined />} onClick={openSourceSelector}>
        {buttonText || 'Select Image'}
      </Button>

      <Modal
        open={sourceModal}
        onCancel={() => setSourceModal(false)}
        footer={null}
        title="Choose Image Source"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button block onClick={() => { setSourceModal(false); setLibraryOpen('user'); }}>
            My Library
          </Button>
          <Button block onClick={() => { setSourceModal(false); fileInputRef.current?.click(); }}>
            Upload New Image
          </Button>
          <Button block onClick={() => { setSourceModal(false); setLibraryOpen('common'); }}>
            Global Asset Library
          </Button>
        </Space>
      </Modal>

      <IconLibraryPicker
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={handleLibrarySelect}
        token={token}
        s3BaseUrl={s3BaseUrl}
        prefix={thumbPrefix || prefix}
        apiPath={libraryType === 'common' ? '/ts/get-s3-common-files' : '/ts/get-s3-files'}
        max={libraryType === 'common' ? 72 : undefined}
        title={libraryType === 'common' ? 'Select Global Asset' : undefined}
        description={libraryType === 'common' ? 'Select an image from the global asset library.' : undefined}
        globalAssetLibrary={libraryType === 'common'}
        initialRatio={libraryType === 'common' ? ratioKey : undefined}
      />

      <Modal
        open={cropModalVisible}
        onCancel={() => setCropModalVisible(false)}
        onOk={handleUpload}
        okButtonProps={{ loading: uploading }}
        title={`Crop Image (${{ '16:9': 'Carousel', '1:1': 'Square', '3:1': 'Banner' }[ratioText] ?? ratioText})`}
        width={860}
        maskClosable={false}
      >
        <Spin spinning={uploading}>
          <div
            ref={cropperRef}
            style={{
              width: '100%',
              height: 420,
              position: 'relative',
              backgroundColor,
              borderRadius: 8,
            }}
          >
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={width / height}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                restrictPosition={!!snapTheFrame}
                onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
              />
            )}
          </div>

          <div style={{ marginTop: 12 }}>
            <Space wrap>
              <Checkbox checked={snapTheFrame} onChange={(e) => setSnapTheFrame(e.target.checked)}>
                Snap the Frame
              </Checkbox>

              <Space>
                <BgColorsOutlined />
                <ColorPicker
                  value={backgroundColor}
                  onChange={(color) => setBackgroundColor(color.toHexString())}
                />
              </Space>

              <Tooltip title="Pick colour from screen">
                <Button icon={<AimOutlined />} onClick={handleScreenPick}>
                  Screen Colour Picker
                </Button>
              </Tooltip>
            </Space>
          </div>

          <div style={{ marginTop: 12 }}>
            <Slider
              min={0.1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(value) => {
                if (snapTheFrame && value < 1) setZoom(1);
                else setZoom(value);
              }}
            />
          </div>

          {isLowResolutionCrop && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#d4380d' }}>
              Warning: this selection is lower resolution than the target size ({width}x{height}) - it will look
              blurry once displayed. Zoom out or choose a higher-resolution image.
            </div>
          )}

          <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
            Tip: If the crop does not meet your expectation, please try another image.
          </div>
        </Spin>
      </Modal>
    </>
  );
};

export default ImageUploader;
